import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import styles from "./QuestionTypes.module.css";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { handleErrorMessage } from "../../../../../redux/actions/classes";
import { useDispatch } from "react-redux";
import { validateQuestion } from "../../../../../utils";
import { useTranslation } from "react-i18next";

const CheckboxesQuestion = forwardRef(
  (
    { initialData = {}, onMoveUp, onMoveDonw, onDeleteQuestion },
    ref
  ) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [question, setQuestion] = useState("");
    const [options, setOptions] = useState(["", ""]);
    const [maxMarks, setMaxMarks] = useState("");
    const [isRequired, setIsRequired] = useState(false);
    const [correctAnswers, setCorrectAnswers] = useState([]);
    const [focusedIndex, setFocusedIndex] = useState(null);
    const containerRef = useRef(null);

    // 🔄 Sync initialData only once on mount
    const initializedRef = useRef(false);
    useEffect(() => {
      if (initializedRef.current || !initialData) return;
      initializedRef.current = true;

      setQuestion(
        initialData.questionText ?? initialData.text ?? initialData.question ?? ""
      );

      // Normalize choices
      const srcChoices = Array.isArray(initialData.choices)
        ? initialData.choices
        : Array.isArray(initialData.choice)
        ? initialData.choice
        : [];

      if (srcChoices.length > 0) {
        setOptions(
          srcChoices.map(
            (c) => c.choiceText ?? c.choice_text ?? c.text ?? ""
          )
        );

        const correctIdxs = srcChoices
          .map((c, idx) => ((c.isCorrect || c.is_correct) ? idx : -1))
          .filter((i) => i >= 0);
        setCorrectAnswers(correctIdxs);
      }

      // Marks (support legacy keys)
      const marksValue =
        initialData.maxMarks ??
        initialData.marks ??
        initialData.max_marks ??
        "";
      setMaxMarks(marksValue !== "" && marksValue !== null ? String(marksValue) : "");

      // Required (normalize to boolean)
      setIsRequired(
        Boolean(initialData.isMandatory ?? initialData.is_mandatory ?? false)
      );
    }, [initialData]);

    // 👉 Helpers
    const handleOptionChange = (index, value) => {
      const newOptions = [...options];
      newOptions[index] = value;
      setOptions(newOptions);
    };

    const handleAddOption = () => {
      setOptions([...options, ""]);
    };

    const handleRemoveOption = (index) => {
      const newOptions = options.filter((_, i) => i !== index);
      setOptions(newOptions);

      setCorrectAnswers(correctAnswers.filter((i) => i !== index));
    };

    const toggleCorrectAnswer = (index) => {
      if (correctAnswers.includes(index)) {
        setCorrectAnswers(correctAnswers.filter((i) => i !== index));
      } else {
        setCorrectAnswers([...correctAnswers, index]);
      }
    };

    const generateChoiceId = (questionId, idx) =>
      `${questionId ?? "q"}-opt-${idx}`;

    // 👉 Save payload
    const handleSave = () => {
      const errorMessage = validateQuestion({
        question,
        marks: maxMarks,
        options,
        correctAnswer: correctAnswers,
        type: "checkboxes", // ✅ plural
      });

      if (errorMessage) {
        dispatch(handleErrorMessage(errorMessage));
        return null;
      }

      const normalizedChoices = options.map((opt, idx) => ({
        id: generateChoiceId(initialData.id, idx),
        choiceText: opt,
        isCorrect: correctAnswers.includes(idx),
      }));

      return {
        id: initialData.id ?? `q-${Date.now()}`,
        type: "checkboxes",
        questionText: question.trim(),
        choices: normalizedChoices,
        isMandatory: !!isRequired,
        maxMarks: parseInt(maxMarks, 10) || 0,
        icon: initialData.icon ?? null,
      };
    };

    // 🔗 Expose save
    useImperativeHandle(ref, () => ({
      save: handleSave,
    }));

    return (
      <div className={styles.questionContainer}>
        <div className={styles.questionHeader}>
          <div className={styles.questionType}>
            <div className={styles.checkboxIcon}>
              <CheckBoxIcon />
            </div>
            <span>{t("quiz.checkboxes")}</span>
          </div>

          <div className={styles.questionActions}>
            <button className={styles.actionButton} onClick={onMoveUp} type="button">
              <KeyboardArrowUpIcon size={20} />
            </button>
            <button className={styles.actionButton} onClick={onMoveDonw} type="button">
              <KeyboardArrowDownIcon size={20} />
            </button>
            <button className={styles.actionButton} onClick={onDeleteQuestion} type="button">
              <DeleteOutlineIcon size={20} />
            </button>
            <div className={styles.marksContainer}>
              <span>{t("quiz.maxMarks")}</span>
              <input
                min={"0"}
                type="number"
                className={styles.marksInput}
                value={maxMarks}
                onChange={(e) => setMaxMarks(e.target.value)}
              />
            </div>
            <div className={styles.requiredContainer}>
              <span>{t("quiz.required")}</span>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={isRequired}
                  onChange={(e) => setIsRequired(e.target.checked)}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
          </div>
        </div>

        <div className={styles.questionContent}>
          <input
            type="text"
            className={styles.questionInput}
            placeholder={t("quiz.question")}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
          />

          <div className={styles.optionsContainer} ref={containerRef}>
            {options.map((option, index) => (
              <div key={index} className={styles.checkboxItem}>
                <div className={styles.checkboxIcon}></div>
                <div
                  style={{ display: "flex", width: "100%" }}
                  onMouseDown={() => setFocusedIndex(index)}
                >
                  <input
                    type="text"
                    className={`${styles.optionInput} ${
                      focusedIndex === index ? styles.focusedInputField : ""
                    }`}
                    placeholder={t("PollModal.addOption")}
                    value={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                  />
                  {focusedIndex === index ? (
                    <div className={styles.optionActions}>
                      <button
                        className={styles.removeOptionButton}
                        onClick={() => handleRemoveOption(index)}
                        type="button"
                      >
                        <CloseIcon fontSize="small" />
                      </button>

                      <label className={styles.correctCheckbox}>
                        <input
                          type="checkbox"
                          checked={correctAnswers.includes(index)}
                          onChange={() => toggleCorrectAnswer(index)}
                        />
                        <span>{t("quiz.correctAnswer")}</span>
                      </label>
                    </div>
                  ) : correctAnswers.includes(index) ? (
                    <div className={styles.tickMarkOnly}>
                      <CheckIcon />
                    </div>
                  ) : null}
                </div>
              </div>
            ))}

            <button
              className={styles.addOptionButton}
              onClick={handleAddOption}
              type="button"
            >
              <AddCircleOutlineIcon sx={{ color: "#F33070" }} />
            </button>
          </div>
        </div>
      </div>
    );
  }
);

export default CheckboxesQuestion;
